/** Dashboard wire types for backend/src/parcels. Keep local: shared/api is a repo symlink. */
export const seatKinds = ['homeowner', 'flipper', 'prime', 'nested_gc', 'carpenter', 'electrician', 'roofer', 'abc', 'agent', 'title', 'attorney'] as const;
export type SeatKind = typeof seatKinds[number];
export const isSeatKind = (value: string | undefined): value is SeatKind => seatKinds.some((seat) => seat === value);
export const seatLabels: Record<SeatKind, string> = {
  homeowner: 'Occupying homeowner watch', flipper: 'Flipper workroom', prime: 'Prime contractor',
  nested_gc: 'Nested GC', carpenter: 'Carpenter', electrician: 'Electrician', roofer: 'Roofer',
  abc: 'ABC supplier', agent: 'Listing agent', title: 'Title file', attorney: 'Attorney file',
};
export const descriptions: Record<SeatKind, string> = {
  homeowner: 'Watch the parties and outstanding furnishing on your home. You control seller consent.',
  flipper: 'Manage the remodel chain and prepare the parcel for sale handoff.',
  prime: 'Review the construction chain and the row-derived affidavit draft.',
  nested_gc: 'Coordinate your carpenter and electrician within the prime’s construction chain.',
  carpenter: 'Review your admitted carpentry seat and the parcel’s furnishing and release records.',
  electrician: 'Review your admitted electrical seat and the parcel’s furnishing and release records.',
  roofer: 'Review supplier furnishing before recording final verification or an explained override.',
  abc: 'Record fulfillment and payment evidence. Unsync preserves every landed row.',
  agent: 'Prepare the sale handoff. A title request records intent; seller consent grants access.',
  title: 'Review the seller-authorized file and export its unsigned packet.',
  attorney: 'Review the seller-authorized file and export its unsigned packet.',
};
export interface ParcelSeat {
  id: string; parcelId: string; seat: SeatKind; contractorId: string | null; companyName: string | null;
  admittedAt: string | null; invitedAt: string | null; consentedAt: string | null; consentedBy: string | null;
  statuteBasis: string | null; feedSynced: boolean;
}
export interface Delivery {
  id: string; supplierSeatId: string; supplierCompany: string; kind: 'delivered' | 'will_call' | 'special_fab';
  eventDate: string; paid: boolean; note: string | null; attachments: string[];
  source: 'manual' | 'feed'; nooEligible: boolean; paymentMarkedAt: string | null;
}
export interface ParcelPacket {
  parcelId: string; county: string; folio: string; address: string; legalOwner: string | null;
  holdFinal: boolean; holdFinalReleaseBlocked: boolean; holdFinalBanner: string | null;
  titleRequested: boolean; affidavit: string; cleanDraftEligible: boolean;
  affidavitRows: Array<{ deliveryId?: string; party: string; scope: string; record: string; exception: boolean }>;
  deliveries: Delivery[]; ridgeLine: 'blocked' | 'need-verify' | 'verified' | 'override'; lgVerified: boolean;
  overrideReason: string | null; ownPdf: { reference: string; lgVerified: false } | null;
  ownPdfNotLgVerified: boolean;
}
export interface ParcelSnapshot {
  parcel: { id: string; county: string; folio: string; address: string; legalOwner: string | null; titleRequested: boolean; holdFinal: boolean };
  seats: ParcelSeat[]; packet: ParcelPacket; readOnly: boolean; viewAs: SeatKind | null;
  /** Public-door responses confirm which seat the capability token resolved to. */
  capability?: { parcelId: string; seat: SeatKind } | null;
}
export const isAdmitted = (seat: ParcelSeat) => !!seat.admittedAt &&
  (!['title', 'attorney'].includes(seat.seat) || !!(seat.consentedAt && seat.consentedBy));
export const inviteRoles: Partial<Record<SeatKind, SeatKind[]>> = {
  homeowner: ['prime', 'title', 'attorney'], flipper: ['prime', 'title', 'attorney'],
  prime: ['nested_gc', 'roofer'], nested_gc: ['carpenter', 'electrician'], roofer: ['abc'], agent: ['title', 'attorney'],
};
export const parcelDoor = (id: string, seat: SeatKind) => `/parcels/${encodeURIComponent(id)}/${seat}`;

/**
 * Seat capability storage, scoped to one parcel+seat so a token never bleeds
 * onto another door. sessionStorage keeps the URL shareable after the query
 * string is consumed; a blocked storage (private mode) degrades to the URL.
 */
const tokenKey = (id: string, seat: string) => `lienguardian.seat.${id}.${seat}`;
export function doorToken(id: string, seat: string, fromQuery: string | null): string | null {
  try {
    if (fromQuery) {
      window.sessionStorage.setItem(tokenKey(id, seat), fromQuery);
      return fromQuery;
    }
    return window.sessionStorage.getItem(tokenKey(id, seat));
  } catch {
    return fromQuery;
  }
}
export const UUID_PATTERN = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
export const isParcelId = (id: string | undefined): id is string => !!id && new RegExp(`^${UUID_PATTERN}$`).test(id);

export interface ParcelMembership {
  graphParcelId: string; county: string; folio: string; address: string; legalOwner: string | null;
  membershipId: string; seat: SeatKind;
  state: 'ready' | 'admission_required' | 'seller_consent_required' | 'support_view_as_only';
  mode: 'watch' | 'file'; targetUrl: string | null; readOnly: boolean;
  occupying?: boolean;
  jobIds?: string[]; propertyIds?: string[]; source?: { kind: 'job' | 'property'; id: string };
}
/** Only accept the canonical local customer target from the provisioning contract. */
export function membershipTarget(row: ParcelMembership): string | null {
  return row.state === 'ready' && isParcelId(row.graphParcelId) && isSeatKind(row.seat) &&
    row.targetUrl === `/parcels/${row.graphParcelId}` ? row.targetUrl : null;
}
